import { FC, useMemo } from 'react';
import { useNavigate, generatePath } from 'react-router';
import { Outage } from '@/apis/outages/types/outagesApi';
import { Task } from '@/apis/tasks/types/tasksApi';

import {
  Icon,
  Stack,
  TextTruncate,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { OutageSite } from '@/pages/Outages/[ID]/components';
import {
  CollapsibleChipList,
  DataItems,
  SemiCircleProgressBar,
} from '@/components';
import { formatDateString, getRiskColor, getRiskLabel } from '@/utils';
import { OUTAGE_TASK_ROUTE } from '@/pages/Tasks/routes';

import '@/pages/Outages/[ID]/components/OutageDetails/OutageDetails.scss';

const OUTAGE_TOOLTIP_TEXT = `Outage Information

This section provides key details about the outage, including start and end dates, scope of work, tasks being performed, and any related notes.

Reviewing this information helps support planning, coordination, and safe execution of outage activities.`;

const getTaskNumber = (task: Task) => task.description;

const getTooltip = (task: Task) => task.taskNumber;

interface OutageDetailsProps {
  outage: Outage;
  tasks: Task[];
}

export const OutageDetails: FC<OutageDetailsProps> = ({ outage, tasks }) => {
  const navigate = useNavigate();

  const handleTaskClick = (task: Task) => {
    if (!task.taskNumber) return;

    navigate(
      generatePath(OUTAGE_TASK_ROUTE, {
        outageId: outage.outageId.toString(),
        taskId: task.taskNumber,
      }),
    );
  };

  const dataItems = useMemo(
    () => [
      {
        key: 'outageID',
        label: 'Outage ID',
        value: outage?.outageId ?? 'N/A',
        single: true,
        inline: true,
      },
      {
        key: 'startDate',
        label: 'Start date',
        value: outage?.outageStart
          ? formatDateString(outage.outageStart, 'MMMM Do, YYYY')
          : 'N/A',
      },
      {
        key: 'endDate',
        label: 'End date',
        value: outage?.outageEnd
          ? formatDateString(outage.outageEnd, 'MMMM Do, YYYY')
          : 'N/A',
      },
      {
        key: 'notes',
        label: 'Notes',
        value: (
          <TextTruncate width="100%" showMoreText showLessText lines={2}>
            {outage.notes || 'N/A'}
          </TextTruncate>
        ),
        single: true,
      },
      {
        key: 'tasks',
        label: 'Tasks',
        value: (
          <CollapsibleChipList
            items={tasks}
            maxVisible={8}
            getKey={getTaskNumber}
            getLabel={getTaskNumber}
            getTooltip={getTooltip}
            onClick={handleTaskClick}
          />
        ),
        single: true,
      },
    ],
    [outage],
  );

  return (
    <div className="outage">
      <div className="outage__info-details">
        <Stack direction="row" gap={8}>
          <Typography color="var(--color-neutral)" tag="textdefault_strong">
            Outage Details
          </Typography>
          <Tooltip title={OUTAGE_TOOLTIP_TEXT} arrow>
            <Icon
              icon="IconInformation"
              color="var(--color-default)"
              iconSize={0.8}
            />
          </Tooltip>
        </Stack>
        <DataItems items={dataItems} />
      </div>
      <div className="outage__risk">
        <Stack gap={24}>
          <Typography tag="textdefault_strong">
            Overall Assessed Risk
          </Typography>
          <SemiCircleProgressBar
            progress={outage.outagePercentile}
            size={200}
            strokeWidth={12}
            color={getRiskColor(outage.outagePercentile)}
            labelText={getRiskLabel(outage.outagePercentile)}
            mode="label"
            showLabel
          />
        </Stack>
      </div>
      <OutageSite siteId={outage.siteId} />
    </div>
  );
};
